import Link from "next/link";
import type { FeaturedPrice } from "@/lib/api";
import { getEmoji } from "@/lib/emoji";
import { productHref } from "@/lib/product-links";

const tl = (v: number) => v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (v: number) => Math.abs(v).toLocaleString("tr-TR", { maximumFractionDigits: 1 });
const dayTr = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });

// Haftalık %35 üstü değişim çoğunlukla kaynak karışması; şeritte güven veren akış
// gösterilir (PriceTicker ile aynı eşik). Uç hareketler ürün sayfasında görünür.
const MAX_CHANGE = 35;

function Entry({ item, dup }: { item: FeaturedPrice; dup?: boolean }) {
  const change = item.changePct != null && Math.abs(item.changePct) <= MAX_CHANGE ? item.changePct : null;
  const up = change != null && change > 0.05;
  const down = change != null && change < -0.05;
  return (
    <Link
      href={productHref(item)}
      prefetch={false}
      tabIndex={dup ? -1 : undefined}
      aria-hidden={dup || undefined}
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap pr-6 hover:underline"
      title={`${item.productName}: ${item.marketCount} halin orta fiyatı, ${dayTr(item.recordedDate)}. 7 günlük değişim ürün sayfasındakiyle aynı hesaptır: aynı hal ve çeşidin son 7 günü, önceki 7 günle karşılaştırılır.`}
    >
      <span aria-hidden>{getEmoji(item.productSlug, item.categorySlug)}</span>
      <span className="font-medium">{item.productName}</span>
      <span className="font-(family-name:--font-mono) font-semibold tabular-nums">₺{tl(item.avgPrice)}</span>
      {change != null ? (
        <span
          className={`font-(family-name:--font-mono) text-xs tabular-nums ${up ? "text-(--trend-up)" : down ? "text-(--trend-down)" : "opacity-60"}`}
          aria-label={`aynı hal ve çeşitte son 7 gün ${up ? "artış" : down ? "düşüş" : "değişim"} yüzde ${pct(change)}`}
        >
          7g {up ? "▲" : down ? "▼" : "■"}{pct(change)}%
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Üst şerit içeriği: ürün başına haller arası ortalama fiyat ve haftalık değişim.
 * `marquee` açıksa liste iki kez basılır ve -50% kaydırılır. Aralık `gap` değil öğe
 * içi `pr-6`: gap ile yarım liste genişliği yarım aralık kısa kalıyor, döngü her turda
 * sekiyordu. Animasyon kendi katmanında (will-change) koşar, ana iş parçacığını beklemez;
 * hareket azaltma tercihinde animasyon durur, liste yatay kaydırılabilir kalır.
 */
export default function PriceStrip({ items, marquee, speed }: { items: FeaturedPrice[]; marquee: boolean; speed?: number | null }) {
  if (!items.length) return null;
  const date = items[0]!.recordedDate;
  // scroll_speed px/s; öğe başına ortalama ~190 px. Değer yoksa 40 px/s.
  const duration = Math.max(20, Math.round((items.length * 190) / Math.max(10, speed || 40)));
  const list = marquee ? [...items, ...items] : items;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <Link href="/hal-fiyatlari" prefetch={false} className="flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide hover:underline">
        <span className="live-dot-sm" aria-hidden />
        <span className="hidden sm:inline">Hal orta fiyatları</span>
        <span className="font-normal normal-case opacity-70">{dayTr(date)}</span>
      </Link>
      <div className={`min-w-0 flex-1 ${marquee ? "ticker-mask overflow-hidden motion-reduce:overflow-x-auto" : "overflow-x-auto [scrollbar-width:none]"}`}>
        <div
          className={`flex w-max items-center text-sm ${marquee ? "strip-track" : ""}`}
          style={marquee ? { animationDuration: `${duration}s` } : undefined}
        >
          {list.map((item, i) => (
            <Entry key={`${item.productSlug}-${i}`} item={item} dup={i >= items.length} />
          ))}
        </div>
      </div>
      <Link href="/hal-fiyatlari" prefetch={false} className="hidden shrink-0 text-xs font-semibold hover:underline md:inline">
        Tüm fiyatlar →
      </Link>
    </div>
  );
}
